import { TextField, IconButton, Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { StyleObj } from '../../@types';
import { Search, Close } from '@mui/icons-material';
import TableActionButton from '../atoms/TableActionButton';
import { useBreakpoints } from '../../hooks/useBreakpoints';
import { mergeSx } from '../../utils/styles';

type SearchTableDataProps = {
  onSearch: (value: string | null) => void;
};

const styles: StyleObj = {
  container: {
    display: 'flex',
    alignItems: 'center',
  },
  search: (theme) => ({
    width: 200,
    padding: 0,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      flex: 1,
    },
  }),
  searchIcon: {
    fontSize: {
      xs: '1rem',
      md: '1.5rem',
    },
  },
  iconButton: {
    p: { xs: 0.5, md: 1 },
  },
} as const;

const SearchTableData = ({ onSearch }: SearchTableDataProps) => {
  const [search, setSearch] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const location = useLocation();
  const { isMobile } = useBreakpoints();

  const isValidSearchTerm = search.trim() !== '';

  useEffect(() => {
    setSearch('');
    setIsExpanded(false);
  }, [location.pathname]);

  const handleSearchValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);

    if (event.target.value === '') {
      onSearch(null);
    }
  };

  const handleEnterKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && isValidSearchTerm) {
      onSearch(search);
    }
  };

  const handleToggleSearch = () => {
    if (isExpanded && search) {
      setSearch('');
      onSearch(null);
    }
    setIsExpanded(!isExpanded);
  };

  if (isMobile && !isExpanded) {
    return <TableActionButton Icon={Search} onClick={handleToggleSearch} sx={styles.iconButton} />;
  }

  return (
    <Box sx={mergeSx(styles.container, { mr: isExpanded ? 1 : 0 })}>
      <TextField
        placeholder='Search'
        value={search}
        onChange={handleSearchValueChange}
        onKeyDown={handleEnterKeyPress}
        name='search'
        sx={styles.search}
        autoFocus={isMobile && isExpanded}
        slotProps={{
          input: {
            startAdornment: <Search sx={styles.searchIcon} />,
            endAdornment: isMobile && isExpanded && (
              <IconButton onClick={handleToggleSearch} size='small' sx={{ p: 0.5 }}>
                <Close sx={styles.searchIcon} />
              </IconButton>
            ),
          },
        }}
      />
    </Box>
  );
};

export default SearchTableData;
